import liquidEngine from 'FlowBuilder/components/ActionViews/liquidEngine'
import useLanguage from 'FlowBuilder/hooks/useLanguageFlow'
import { newrelic } from 'lib/newrelic'
const MOVEMENT_PAGE_LOADED = 'movementPageLoaded';
import FieldEdit from 'FlowBuilder/components/ActionViews/FieldEdit'

export default function TemplateRender({ htmlContent, context = {}, componentMap }) {
  // Reference to hold component target nodes
  const language = useLanguage()
  const isPreview = useSel(s => s.local.isPreview)
  const [componentTargets, setComponentTargets] = useState([]);
  const [progressBarTargets, setProgressBarTargets] = useState([]);
  const [questionNumberTargets, setQuestionNumberTargets] = useState([]);
  const [signatureCountTargets, setSignatureCountTargets] = useState([]);
  const [editTargets, setEditTargets] = useState([]);
  // Reference to hold the container element
  const containerRef = useRef(null);
  const templateFieldsConfig = useSel(s => s.builder?.flowTemplate?.fields || s.taker?.flow?.template?.fields || [])

  useEffect(() => {
    // Create temporary container to parse HTML
    async function processTemplate() {
      try {
        var templateVars = { ...(context.template || {}) }

        // Render the template with the provided context
        if (isPreview) {
          const allSlugs = templateFieldsConfig.map(f => f.slug)
          // add empty values for all fields
          allSlugs.forEach(slug => {
            if (!templateVars[slug]) {
              templateVars[slug] = ''
            }
          })

          var newContext = {
            ...context,
            template: _.mapValues(templateVars, (value, slug) => {
              const fieldConfig = templateFieldsConfig.find(f => f.slug === slug)
              // if a setup field then don't have inline editing
              if (fieldConfig?.setup) {
                return value
              } else {
                return `<editfield id="${_.escape(slug)}"></editfield>`
              }
            })
          }
          var renderedContent = await liquidEngine.parseAndRender(htmlContent, newContext);
        } else {
          const templateEntries = await Promise.all(
            Object.entries(templateVars).map(async ([slug, value]) => {
              try {
                const renderedValue = await liquidEngine.parseAndRender(value, context);
                return [slug, renderedValue];
              } catch (e) {
                // If rendering a specific field fails, log the error and return the original value
                console.error(`Error rendering template field ${slug}:`, e);
                newrelic?.noticeError(e);
                return [slug, value];
              }
            })
          );
          var renderedContent = await liquidEngine.parseAndRender(htmlContent, {
              ...context,
              template: Object.fromEntries(templateEntries)
            });
        }

        // Create temporary container to parse HTML
        const temp = document.createElement('div');
        temp.innerHTML = renderedContent;

        // ensure scripts inside templates run
        const scripts = temp.getElementsByTagName('script');
        Array.from(scripts).forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });

        const parent = containerRef.current.parentElement;

        // Clear any existing content
        while (containerRef.current.previousSibling) {
          parent.removeChild(containerRef.current.previousSibling);
        }

        // Insert all elements before our container div
        while (temp.firstChild) {
          parent.insertBefore(temp.firstChild, containerRef.current);
        }

        // Find component targets in the parent
        const surveyTargets = parent.querySelectorAll('surveyform');
        setComponentTargets(Array.from(surveyTargets));

        // Find progress bar targets in the parent
        const progressTargets = parent.querySelectorAll('progressbar');
        setProgressBarTargets(Array.from(progressTargets));

        // Find question number targets in the parent
        const questionNumberTargets = parent.querySelectorAll('questionnumber');
        setQuestionNumberTargets(Array.from(questionNumberTargets));

        // Find signature count targets in the parent
        const signatureCountTargets = parent.querySelectorAll('signaturecount');
        setSignatureCountTargets(Array.from(signatureCountTargets));

        // Dispatch custom event after content is added
        const event = new CustomEvent(MOVEMENT_PAGE_LOADED, {
          detail: {
            content: renderedContent,
            timestamp: new Date(),
            parent: parent
          },
          bubbles: true  // This allows the event to bubble up through the DOM
        });
        containerRef.current.dispatchEvent(event);

        if (!isPreview) return;

        const editT = _.map(newContext.template, (value, slug) => {
          const targets = parent.querySelectorAll(`editfield[id="${slug}"]`);
          return targets ? { targets: Array.from(targets), slug } : null;
        })
        const editTNoNulls = _.compact(editT);
        setEditTargets(editTNoNulls);
      } catch (error) {
        console.error('Error processing template:', error);
      }
    }

    processTemplate();

    // Cleanup
    return () => {
      setComponentTargets([]);
      setProgressBarTargets([]);
      setQuestionNumberTargets([]);
      setSignatureCountTargets([]);
      setEditTargets([]);
      while (containerRef.current.previousSibling) {
        const parent = containerRef.current.parentElement;
        parent.removeChild(containerRef.current.previousSibling);
      }
    };
  }, [htmlContent, language]) // Only run when the HTML content changes

  return (
    <>
      {/* Hidden marker element */}
      <div ref={containerRef} style={{ display: 'none' }} />
      {componentTargets.map((target, index) =>
        ReactDOM.createPortal(
          componentMap.TakeSurveyForm,
          target,
          `take-survey-form-${index}`
        )
      )}
      {progressBarTargets.map((target, index) =>
        ReactDOM.createPortal(
          componentMap.ProgressBar,
          target,
          `progress-bar-${index}`
        )
      )}
      {questionNumberTargets.map((target, index) =>
        ReactDOM.createPortal(
          componentMap.QuestionNumber,
          target,
          `question-number-${index}`
        )
      )}
      {signatureCountTargets.map((target, index) =>
        ReactDOM.createPortal(
          componentMap.SignatureCount,
          target,
          `signature-count-${index}`
        )
      )}
      {editTargets.length > 0 && editTargets.map(({ targets, slug }, index) => {
        if (!targets || targets.length === 0) return null;

        return targets.map((target, index) => {
          return ReactDOM.createPortal(
            <FieldEdit slug={slug} />,
            target,
            `field-edit-${slug}-${index}`
          )
        })
      })}
    </>
  );
}
